// src/components/ui/InfoTip.jsx
//
// A field's explanation kept OUT of the layout: a small ⓘ button after the label whose text appears on mouse
// hover, on keyboard focus and on tap (a native `title` shows on hover only, so keyboard and touch users would
// never see it). The text is the button's accessible DESCRIPTION (aria-describedby), so a screen reader reads it
// when the button is reached; `label` is its accessible name ("Informazioni: Nome").
// Escape closes the tooltip and stops there — inside a modal it must not also close the modal.
// Presentational: no copy of its own, theme tokens only.
import React, { useId, useState } from 'react';
import { Info } from 'lucide-react';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';

const WRAP = { position: 'relative', display: 'inline-flex', verticalAlign: 'middle' };
const BUTTON = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px',
  padding: 0, border: 'none', borderRadius: RADII.pill, background: 'none', cursor: 'help',
  color: COLORS.textTertiary,
};
const BUBBLE = {
  position: 'absolute', top: 'calc(100% + 6px)', left: '-8px', zIndex: 5, width: 'max-content', maxWidth: '300px',
  padding: '8px 10px', borderRadius: RADII.controlInner, background: COLORS.ink, color: COLORS.surface,
  fontFamily: FONT_SANS, fontSize: '12px', fontWeight: 400, lineHeight: 1.5, letterSpacing: 'normal',
  textTransform: 'none', whiteSpace: 'normal', boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
};

export default function InfoTip({ text, label }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  return (
    <span style={WRAP}>
      <button
        type="button"
        aria-label={label}
        aria-describedby={id}
        aria-expanded={open}
        style={BUTTON}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); }
        }}
      >
        <Info size={14} aria-hidden="true" />
      </button>
      {/* Always in the DOM (the button's description), shown only while open. */}
      <span id={id} role="tooltip" style={open ? BUBBLE : { display: 'none' }}>{text}</span>
    </span>
  );
}
